import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { schemaApi, serviceCatalogApi } from '@/services/api';
import { useApps } from '@/contexts/AppsContext';
import { ServicesTab, GraphSchemaTab, PipelineView, ExtractionView } from '@/components/schema/SchemaEditor';
import AppDescriptionTab from '@/components/schema/AppDescriptionTab';
import CuriosityPromptsTab from '@/components/schema/CuriosityPromptsTab';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Save, Loader2, FileText, Layers, GitBranch, Workflow, Download, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
  { id: 'app', label: 'App', icon: FileText },
  { id: 'services', label: 'Services', icon: Layers },
  { id: 'schema', label: 'Graph Schema', icon: GitBranch },
  { id: 'curiosity', label: 'Curiosity', icon: Sparkles },
  { id: 'pipeline', label: 'Pipeline', icon: Workflow },
  { id: 'extraction', label: 'Extraction', icon: Download },
];

export default function SchemaPage() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const { apps, currentApp, selectApp } = useApps();

  // Sync URL :appId to sidebar context
  useEffect(() => {
    if (appId && (!currentApp || currentApp.appId !== appId)) {
      const app = apps.find((a) => a.appId === appId);
      if (app) selectApp(app);
    }
  }, [appId, apps, currentApp, selectApp]);

  const [schema, setSchema] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('app');

  const fetchSchema = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await schemaApi.get(appId);
      setSchema(res.data?.data);
    } catch (err) {
      if (err.response?.status !== 404) {
        setError('Failed to load schema');
      }
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    fetchSchema();
    serviceCatalogApi.list()
      .then((res) => {
        const data = res.data?.data;
        setServices(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
  }, [fetchSchema]);

  // Count enabled services for tab badge
  const enabledCount = useMemo(() => {
    if (!schema?.subscriptions) return 0;
    const subs = schema.subscriptions;
    return [
      ...(subs.classifiers || []),
      ...(subs.enrichers || []),
      ...(subs.system || []),
    ].filter(s => s.enabled).length;
  }, [schema?.subscriptions]);

  function handleChange(updated) {
    setSchema(updated);
    setDirty(true);
  }

  async function handleSave() {
    try {
      setSaving(true);
      setError('');
      const res = await schemaApi.update(appId, schema);
      setSchema(res.data?.data);
      setDirty(false);
      toast.success('Schema saved');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save schema');
      toast.error('Failed to save schema');
    } finally {
      setSaving(false);
    }
  }

  function handleDeleteCustomNode(name) {
    handleChange({
      ...schema,
      custom_node_types: (schema.custom_node_types || []).filter(n => n.name !== name),
    });
  }

  function handleDeleteCustomEdge(name) {
    handleChange({
      ...schema,
      custom_edge_types: (schema.custom_edge_types || []).filter(e => e.name !== name),
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!schema) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-muted-foreground">
          This app doesn't have a schema yet. Create a new app using the wizard to generate one.
        </p>
        <Button variant="outline" onClick={() => navigate('/apps/new')}>
          Create New App
        </Button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b bg-background/80 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold">{currentApp?.name || 'App'} Schema</h1>
          {schema.version && (
            <Badge variant="outline" className="text-[10px]">v{schema.version}</Badge>
          )}
          {schema.authorship_model && (
            <Badge variant="secondary" className="text-[10px]">{schema.authorship_model}</Badge>
          )}
        </div>
        <Button onClick={handleSave} disabled={saving || !dirty} size="sm">
          {saving ? (
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Save className="mr-2 h-3.5 w-3.5" />
          )}
          Save Changes
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b px-6">
        <div className="flex gap-1">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {tab.label}
                {tab.id === 'services' && enabledCount > 0 && (
                  <Badge variant="secondary" className="text-[10px] ml-0.5 px-1.5 py-0">{enabledCount}</Badge>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mx-6 mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="max-w-4xl">
          {activeTab === 'app' && (
            <AppDescriptionTab schema={schema} onChange={handleChange} />
          )}

          {activeTab === 'services' && (
            <ServicesTab
              schema={schema}
              services={services}
              onChange={handleChange}
            />
          )}

          {activeTab === 'schema' && (
            <GraphSchemaTab
              schema={schema}
              services={services}
              onDeleteCustomNode={handleDeleteCustomNode}
              onDeleteCustomEdge={handleDeleteCustomEdge}
            />
          )}

          {activeTab === 'curiosity' && (
            <CuriosityPromptsTab schema={schema} onChange={handleChange} />
          )}

          {activeTab === 'pipeline' && (
            <Card>
              <CardContent className="p-4">
                <PipelineView
                  config={schema.pipeline_config}
                  onChange={(config) => handleChange({ ...schema, pipeline_config: config })}
                />
              </CardContent>
            </Card>
          )}

          {activeTab === 'extraction' && (
            <Card>
              <CardContent className="p-4">
                <ExtractionView schema={schema} onChange={handleChange} />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
